/**
 * This is store module that handles search functionalities. It keeps
 * query data sent to search API and returned results. It also manages selected
 * search result.
 */
/**
 * All hew state elements added to this module should also be added
 * to the validation schema at /src/store/storage/schema.js
 */

/* eslint-disable no-param-reassign */
import * as tt from '@tomtom-international/web-sdk-services/dist/services.min';

import {
    boundingRect,
    miniatureForRect,
    titleResult,
    addressResults,
} from '../helpers';

/**
 * TomTom's fuzzy search service.
 * {@link https://developer.tomtom.com/search-api/search-api-documentation-search/fuzzy-search}
 */
const { services: { fuzzySearch } } = tt;

/**
 * Mutation names
 */
const mutationNames = {
    SET_QUERY: 'setQuery',
    SET_PENDING: 'setPending',
    SET_RESULT: 'setResult',
    SELECT: 'select',
    CLEAR_SELECTION: 'clearSelection',
    SET_MINIATURE: 'setMiniature',
    CLEAR_MINIATURE: 'clearMiniature',
    SET_PANEL_HIDDEN: 'setPanelHidden',
    SHOW_MINIATURE_LOADER: 'showMiniatureLoader',
    HIDE_MINIATURE_LOADER: 'hideMiniatureLoader',
    SET_MINIATURE_ERROR: 'setMiniatureError',
};

/**
 * Module state
 */
const state = {
    /**
     * Query string, keeps current/last query written by the user.
     * @type {string}
     */
    query: '',

    /**
     * Flag indicating whether there's a query currently pending.
     * @type {boolean}
     */
    pending: false,

    /**
     * Summary of search parameters, e.g. total results, query time,
     * returned from
     * {@link https://developer.tomtom.com/search-api/search-api-documentation-search/fuzzy-search |
     * TomTom's Fuzzy Search}.
     *
     * @type {Object} summary
     */
    summary: {},

    /**
      * Store only address, position, poi formatted elements from query
      * results of TomTom search API.
      * {@link https://developer.tomtom.com/search-api/search-api-documentation-search/fuzzy-search}
      * @type {Array}
      */
    rawResults: [],

    /**
     * User's currently selected/clicked search result. Clicking on an object
     * should center the map on it. Contains detailed data about selected place.
     * @type {Object}
     */
    selection: null,

    /**
     * Miniature URL for currently selected search item determined by
     * {@link selection}.
     * @type {?string}
     */
    miniature: null,

    /**
     * Flag determining whether the search column is visible on screen.
     * @type {boolean}
     */
    panelHidden: false,

    /**
     * Flag determinig if loader of miniature is shown or hidden.
     * @type {boolean}
     */
    isLoadingMiniature: true,

    /**
     * Flag determining if miniature load was successfull.
     * @type {boolean}
     */
    hasMiniatureError: false,
};

/**
 * Module getters
 */
const getters = {
    /**
     * Returns boolean describing if there is selected object.
     * @type {boolean}
     */
    hasSelection: ({ selection }) => !!selection,

    /**
     * Returns string describing if Point Of Interest (POI) object exists
     * in data for selected location.
     * @type {boolean}
     */
    hasPoi: ({ selection }, { hasSelection }) => (
        hasSelection && !!selection.poi
    ),

    /**
     * Details for selected search result.
     * Contains title, address and coordinates.
     * When there is no selection, `null` is returned.
     * @type {Object}
     */
    resultDetails: ({ selection }, { hasSelection }) => {
        if (!hasSelection) {
            return null;
        }

        const {
            position: { lat, lng } = {},
        } = selection;

        return {
            title: titleResult(selection),
            detailAddress: addressResults(selection),
            coordinates: `${lat}, ${lng}`,
        };
    },

    /**
     * Results array, created from raw TomTom API results,
     * each raw object additionally contains
     * title and detailed address in special format.
     * @type {Array}
     */
    results: ({ rawResults = [] }) => rawResults.map(
        item => ({
            title: titleResult(item),
            detailAddress: addressResults(item),
            item,
        }),
    ),

    /**
     * Coordinates of selected item's viewport. An array with 2 points
     * (each point represented as array) is returned:
     *  - TL - top left point
     *  - BR - bottom right point
     *  When there is no selection `null` is returned.
     * @type {Array}
     */
    selectedBounds: ({ selection }) => {
        if (!selection) {
            return null;
        }

        const {
            viewport: {
                topLeftPoint: { lng: lngSW, lat: latNE },
                btmRightPoint: { lng: lngNE, lat: latSW },
            },
        } = selection;
        return [[lngSW, latSW], [lngNE, latNE]];
    },

    /**
     * Position of current selection. An object with two properties:
     * `lat` - latitude,
     * `lng` - longitude.
     * When there is no selection, empty object is returned.
     * @type {Object}
     */
    selectedPostition: ({ selection }, { hasSelection }) => (
        hasSelection ? selection.position : {}
    ),

    /**
     * Flag indicating whether the search result list is empty
     * while we're searching for something.
     * @type {boolean}
     */
    noResults: ({ query, pending, rawResults }) => (
        !!query.length && !pending && !rawResults.length
    ),
};

/**
 * Module mutations. Their names are defined as strings-values from map
 * `mutationNames`.
 */
const mutations = {
    /**
     * Sets user's query string.
     * @param {string} query
     */
    [mutationNames.SET_QUERY]: (s, query) => {
        s.query = query;
    },

    /**
     * Sets query's pending status.
     * @param {boolean} pending
     */
    [mutationNames.SET_PENDING]: (s, pending) => {
        s.pending = pending;
    },

    /**
     * Sets query summary and results from given object.
     * @param {Object}
     */
    [mutationNames.SET_RESULT]: (s, { summary, results }) => {
        s.summary = summary;
        s.rawResults = results;
    },

    /**
     * Sets selection, it takes one of the results in object `selection`.
     * @param {Object} selection
     */
    [mutationNames.SELECT]: (s, selection) => {
        s.selection = selection;
    },

    /**
     * Clears selection.
     */
    [mutationNames.CLEAR_SELECTION]: (s) => {
        s.selection = null;
    },

    /**
     * Sets miniature URL for currently selected search result.
     * @param {?string} url - Miniature URL.
     */
    [mutationNames.SET_MINIATURE]: (s, url) => {
        s.miniature = url;
    },

    /**
     * Clears miniature URL.
     */
    [mutationNames.CLEAR_MINIATURE]: (s) => {
        s.miniature = null;
    },

    /**
     * Sets visibility of search column.
     * @param {boolean} hidden
     */
    [mutationNames.SET_PANEL_HIDDEN]: (s, hidden) => {
        s.panelHidden = hidden;
    },

    /**
     * Sets loader visibility.
     */
    [mutationNames.SHOW_MINIATURE_LOADER]: (s) => {
        s.isLoadingMiniature = true;
    },

    /**
     * Hide loader visibility.
     */
    [mutationNames.HIDE_MINIATURE_LOADER]: (s) => {
        s.isLoadingMiniature = false;
    },

    /**
     * Sets error flag for miniature load.
     */
    [mutationNames.SET_MINIATURE_ERROR]: (s, hasError) => {
        s.hasMiniatureError = hasError;
    },
};

/**
 * Module actions
 */
const actions = {
    /**
     * Sets given query as current.
     * If query is too short (length <= 1), clears results.
     * Then performs search.
     * @param {string} query
     */
    setQuery({ commit, dispatch }, query) {
        commit(mutationNames.SET_PENDING, true);
        commit(mutationNames.SET_QUERY, query);

        if (query.length <= 1) {
            commit(mutationNames.SET_RESULT, {});
            commit(mutationNames.SET_PENDING, false);
            return;
        }

        dispatch('performSearch', query);
    },

    /**
     * Performs search action, takes
     * @param {string} query
     * then uses TomTom's fuzzy search
     * with `apiKey` from root state.
     * If query is successful, then results are stored in
     * `rawResults` and `summary` fields of this module's state.
     */
    async performSearch({
        commit, rootState: { apiKey: key, map: { center } },
    }, query) {
        try {
            const options = { key, query };
            if (center) {
                options.center = center;
            }
            const result = await fuzzySearch(options).go();
            commit(mutationNames.SET_RESULT, result);
            commit(mutationNames.SET_PENDING, false);
        } catch {
            // TODO: add error handling for search.
        }
    },

    /**
     * Clears query by setting `query`, `rawResults` and 'summary'
     * to empty values.
     */
    clearQuery({ commit }) {
        commit(mutationNames.SET_QUERY, '');
        commit(mutationNames.SET_RESULT, {});
        commit(mutationNames.SET_PENDING, false);
    },

    /**
     * Selects given object. It triggers fetching of panorama miniature
     * for given search result.
     * @param {Object} selection - The search item to be displayed.
     */
    select({ dispatch }, selection) {
        dispatch('makeSelection', selection);
        dispatch('fetchMiniature', selection);

        dispatch('track/clearMarker', null, { root: true });
    },

    /**
     * Fetches MoMa panorama miniature, which is then shown
     * in search result details.
     * @param {Object} selection - The search item for which panorama has
     * to be downloaded.
     */
    async fetchMiniature(
        { commit, rootGetters: { momaURL } },
        selection,
    ) {
        const { position } = selection;
        const boundingBox = boundingRect(position, 100);

        commit(mutationNames.SHOW_MINIATURE_LOADER);
        commit(mutationNames.SET_MINIATURE_ERROR, false);

        const miniature = await miniatureForRect({
            boundingBox,
            urlBuilder: momaURL,
        });

        try {
            // Load miniature into fetch to extend the loader for the
            // blurring process.
            const controller = new AbortController();
            const { signal } = controller;
            const timeout = setTimeout(() => {
                controller.abort();
                commit(mutationNames.SET_MINIATURE_ERROR, true);
            }, 30000);

            await fetch(miniature, { signal });
            clearTimeout(timeout);
            commit(mutationNames.SET_MINIATURE, miniature);
        } catch (error) {
            commit(mutationNames.SET_MINIATURE_ERROR, true);
        }

        commit(mutationNames.HIDE_MINIATURE_LOADER);
    },

    /**
     * Sets given object as selected, which is one of the results.
     * @param {Object} selection
     */
    makeSelection({ commit }, selection) {
        commit(mutationNames.SELECT, selection);
    },

    /**
     * Action that clears selection.
     */
    clearSelection({ commit }) {
        commit(mutationNames.CLEAR_SELECTION);
        commit(mutationNames.CLEAR_MINIATURE);
    },

    /**
     * Toggles visibility of search column.
     */
    togglePanelHidden({ commit, state: { panelHidden } }) {
        commit(mutationNames.SET_PANEL_HIDDEN, !panelHidden);
    },
};

export default {
    namespaced: true,
    actions,
    getters,
    mutations,
    state,
};
